import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { SalespersonStats } from '@/types/session.ts';
import { VendorCard } from '@/components/vendor/index.ts';
import { buildVendorProfileFromAverages } from '@/utils/vendorProfileBuilder.ts';
import { getCardTier } from '@/types/vendor-kpi.ts';

interface TeamGridProps {
  salespersonStats: SalespersonStats[];
  onSelectMember?: (member: SalespersonStats) => void;
  currentUserId?: string;
}

export default function TeamGrid({ salespersonStats, onSelectMember, currentUserId }: TeamGridProps) {
  const { t } = useTranslation();
  const ranked = useMemo(() => {
    return salespersonStats
      .map((sp) => {
        const profile = buildVendorProfileFromAverages(sp.kpiAveragesJson);
        const globalScore = profile?.globalScore ?? 0;
        return { ...sp, profile, globalScore };
      })
      .sort((a, b) =>
        b.globalScore - a.globalScore ||
        b.sessionCount - a.sessionCount ||
        a.userId.localeCompare(b.userId)
      );
  }, [salespersonStats]);

  if (ranked.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center rounded-xl border border-border bg-muted/30 py-12">
        <p className="text-sm font-medium text-muted-foreground">
          {t('admin.noTeamMembers')}
        </p>
      </div>
    );
  }

  return (
    <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5">
      {ranked.map((member, index) => {
        const initials = `${member.firstName[0]}${member.lastName[0]}`;
        const name = `${member.firstName} ${member.lastName}`;
        const tier = getCardTier(member.globalScore);
        const tierLabel = tier === 'elite' ? 'Elite' : tier === 'pro' ? 'Pro' : 'Standard';
        const tierColor = tier === 'elite' ? '#C9A84C' : tier === 'pro' ? 'var(--primary)' : 'var(--color-gray-400)';
        const isCurrentUser = currentUserId === member.userId;

        return (
          <div key={member.userId} className="relative">
            {/* Position badge */}
            <div className="absolute -left-2 -top-2 z-10 flex h-7 w-7 items-center justify-center rounded-full bg-card border border-border text-xs font-bold text-foreground shadow-sm">
              #{index + 1}
            </div>
            {/* "Tú" badge */}
            {isCurrentUser && (
              <div className="absolute -right-2 -top-2 z-10 rounded-full bg-primary px-2 py-0.5 text-[10px] font-bold text-white shadow-sm">
                {t('common.you')}
              </div>
            )}
            <VendorCard
              name={name}
              initials={initials}
              globalScore={member.globalScore}
              categories={member.profile?.categories ?? []}
              categoryBadge={tierLabel}
              categoryColor={tierColor}
              totalSimulations={member.sessionCount}
              onClick={onSelectMember ? () => onSelectMember(member) : undefined}
            />
          </div>
        );
      })}
    </div>
  );
}
